import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { versiBawaan, versiBolehDipilih } from "@/lib/surveiVersi";
import { KELOMPOK_KEDOKTERAN, KELOMPOK_KEPNAKES, LABEL_KELOMPOK } from "@/lib/survei";

const KOSONG = { nama: "", tanggal_mulai: "", tanggal_selesai: "" };

// Buat/ubah periode survei (nama + tanggal + versi butir). Buka/tutup periode lewat tombol tersendiri di tabel.
// Versi butir & Kelompok (Kedokteran bawaan / Kepnakes, PR #112) hanya dipilih saat MEMBUAT periode; setelah itu tidak bisa
// diganti.
export default function SurveiPeriodeDialog({ open, periode, versi = [], onClose, onSimpan }) {
    const [form, setForm] = useState(KOSONG);
    const [sibuk, setSibuk] = useState(false);
    useEffect(() => {
        if (!open) return;
        setForm(periode ? { nama: periode.nama, tanggal_mulai: periode.tanggal_mulai, tanggal_selesai: periode.tanggal_selesai }
            : { ...KOSONG, versi_butir_id: versiBawaan(versi)?.id || "", kelompok: KELOMPOK_KEDOKTERAN });
    }, [open, periode, versi]);

    const ubah = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const lengkap = form.nama.trim() && form.tanggal_mulai && form.tanggal_selesai && (periode || form.versi_butir_id);
    const simpan = async () => {
        setSibuk(true);
        try { await onSimpan(form); } finally { setSibuk(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{periode ? "Ubah Periode Survei" : "Periode Survei Baru"}</DialogTitle>
                    <DialogDescription>
                        {periode ? "Perubahan tanggal langsung berlaku untuk tautan survei." : "Periode baru dibuat dalam keadaan tertutup. Buka lewat tombol \"Buka\" di tabel."}
                    </DialogDescription>
                </DialogHeader>
                <div className="min-w-0 space-y-3">
                    <div className="space-y-1">
                        <Label htmlFor="survei-periode-nama">Nama periode</Label>
                        <Input id="survei-periode-nama" data-testid="survei-periode-nama" maxLength={100} value={form.nama} onChange={ubah("nama")} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label htmlFor="survei-periode-mulai">Tanggal mulai</Label>
                            <Input id="survei-periode-mulai" data-testid="survei-periode-mulai" type="date" value={form.tanggal_mulai} onChange={ubah("tanggal_mulai")} />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="survei-periode-selesai">Tanggal selesai</Label>
                            <Input id="survei-periode-selesai" data-testid="survei-periode-selesai" type="date" value={form.tanggal_selesai} onChange={ubah("tanggal_selesai")} />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <Label>Kelompok</Label>
                        {periode ? (
                            <p className="text-sm text-slate-700" data-testid="survei-periode-kelompok-tetap">
                                {LABEL_KELOMPOK[periode.kelompok] || LABEL_KELOMPOK[KELOMPOK_KEDOKTERAN]} <span className="text-xs text-slate-500">(tidak bisa diganti)</span>
                            </p>
                        ) : (
                            <Select value={form.kelompok} onValueChange={(v) => setForm((f) => ({ ...f, kelompok: v }))}>
                                <SelectTrigger data-testid="survei-periode-kelompok"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={KELOMPOK_KEDOKTERAN}>{LABEL_KELOMPOK[KELOMPOK_KEDOKTERAN]}</SelectItem>
                                    <SelectItem value={KELOMPOK_KEPNAKES}>{LABEL_KELOMPOK[KELOMPOK_KEPNAKES]} (Keperawatan &amp; Nakes)</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                    <div className="space-y-1">
                        <Label>Versi butir</Label>
                        {periode ? (
                            <p className="text-sm text-slate-700" data-testid="survei-periode-versi-tetap">
                                {periode.versi_butir?.nama || "–"} <span className="text-xs text-slate-500">(tidak bisa diganti)</span>
                            </p>
                        ) : (
                            <Select value={form.versi_butir_id || ""} onValueChange={(v) => setForm((f) => ({ ...f, versi_butir_id: v }))}>
                                <SelectTrigger className="min-w-0 [&>span]:truncate" data-testid="survei-periode-versi"><SelectValue placeholder="Pilih versi butir" /></SelectTrigger>
                                <SelectContent>
                                    {versiBolehDipilih(versi).map((v) => (
                                        <SelectItem key={v.id} value={v.id}>{v.nama} · {v.label} ({v.jumlah_butir} butir)</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Batal</Button>
                    <Button disabled={!lengkap || sibuk} onClick={simpan} className="bg-[#00B9AD] text-[#0F2E30] hover:bg-[#60C0D0]" data-testid="survei-periode-simpan">Simpan</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
